import React, { useState, useEffect } from 'react';

import { Button } from 'app/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { Pencil, Trash } from 'lucide-react';
import { Input } from 'app/components/ui/input';

import DialogProductCategories from '../Add/categories-dialog';
import { DialogUpdateProductCategories } from '../Edit/edit-dialog';
import { AlertProductCategoriesDelete } from '../Delete/delete-confirmation';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/hooks/debounce-hook';
import DialogAddCategory from '../Add/categories-dialog';
import { mediaBaseUrl } from 'utils/api/endpoints';
import { useCourseSlice } from '../../slice';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';

export function DataTable() {
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [openAdd, setOpenAdd] = useState(false);

  const { useLazyGetCourseCategoriesQuery } = useCourseSlice();
  const [
    getCategories,
    { data, error: CategoriesError, isLoading: CategoriesLoading },
  ] = useLazyGetCourseCategoriesQuery();

  const categoriesPagination = {
    totalPages: data?.response?.totalPages,
    pageSize: data?.response?.itemsPerPage,
  };

  const categories = data?.response?.data || [];
  const dispatch = useDispatch();

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'category_id',
      header: 'Category ID',
      cell: ({ row }) => <div>{row.getValue('category_id')}</div>,
    },
    {
      accessorKey: 'category_name',
      header: 'Category Name',
      cell: ({ row }) => <div>{row.getValue('category_name')}</div>,
    },
    {
      accessorKey: 'slug',
      header: 'slug',
      cell: ({ row }) => <div>{row.getValue('slug')}</div>,
    },
    {
      accessorKey: 'image',
      header: 'Image',
      cell: ({ row }) => (
        <div>
          {row.getValue('image') ? (
            <img
              src={mediaBaseUrl + '/products/' + row.getValue('image')}
              alt="Category"
              className="w-5 h-auto"
            />
          ) : (
            <span>No Image</span>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant={'ghost'}>
                <DotsHorizontalIcon className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRow(row.original); // Set the selected row data
                  setOpenUpdate(true); // Open the edit dialog
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRow(row.original); // Set the selected row data
                  setOpenDelete(true); // Open the delete dialog
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: categories,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: categoriesPagination?.totalPages,
    rowCount: categoriesPagination?.pageSize,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  useEffect(() => {
    getCategories({
      page: pagination.pageIndex + 1,
      search: searchDebounce,
    });
  }, [pagination, searchDebounce]);

  return (
    <div className="w-full bg-white  dark:bg-transparent p-4">
      <div className="flex items-center justify-between py-4">
        <Input
          placeholder="Search Categories..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Button onClick={() => setOpenAdd(true)}>Add Category</Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          Page {pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="space-x-2">
          <Button
            variant="link"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </Button>
          <Button
            variant="link"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'Prev'}
          </Button>
          <Button
            variant="link"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'Next'}
          </Button>
          <Button
            variant="link"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </Button>
        </div>
      </div>

      <DialogAddCategory isOpen={openAdd} onClose={setOpenAdd} />

      <DialogUpdateProductCategories
        isOpen={openUpdate}
        onOpenChange={setOpenUpdate}
        Categoriesdata={selectedRow}
      />
      <AlertProductCategoriesDelete
        isOpen={openDelete}
        onOpenChange={setOpenDelete}
        data={selectedRow}
      />
    </div>
  );
}
